import { css } from '@emotion/core';
import { styled, textStyle, pxToRem, Text, Heading } from '@feast-it/ui/light';

export const Card = styled.article<{ isDark?: boolean }>(
  ({ theme, isDark }) =>
    css`
      width: 100%;
      min-width: 0;
      background: ${isDark ? theme.colors.black : theme.colors.white};
      color: ${isDark ? theme.colors.white : 'inherit'};
      border: ${isDark ? `${pxToRem(1)} solid ${theme.colors.white}` : 'none'};
      transition: box-shadow ${theme.transitions.short};
      border-radius: ${pxToRem(12)};
      overflow: hidden;

      &:hover {
        box-shadow: ${theme.shadows[0]};
      }

      &:has(a:focus-visible) {
        box-shadow: ${theme.shadows[0]};
        outline: 2px solid ${theme.colors.mediumBlue};
      }
    `
);

export const StyledLink = styled.a(
  ({ theme }) => css`
    display: block;
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    border-radius: inherit;
    height: 100%;
    width: 100%;

    &:focus-visible {
      outline: 2px solid ${theme.colors.mediumBlue};
      outline-offset: 0;
    }
  `
);

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

export const ImageContainer = styled.div(
  ({ theme }) => css`
    position: relative;
    background-color: ${theme.colors.darkMediumGrey};
    aspect-ratio: 4 / 3;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `
);

export const QuickResponseWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing[1]};
    width: fit-content;
    position: absolute;
    top: ${theme.spacing[2]};
    left: ${theme.spacing[2]};
    background-color: ${theme.colors.white};
    border-radius: ${pxToRem(14)};
    padding: ${theme.spacing[1]} ${theme.spacing[2]};
  `
);

export const BasePriceText = styled(Text)(
  ({ theme }) => css`
    background-color: ${theme.colors.white};
    position: absolute;
    bottom: ${theme.spacing[2]};
    left: ${theme.spacing[2]};
    padding: ${theme.spacing[1]} ${theme.spacing[2]};
    border-radius: ${theme.radii[0]};
  `
);

export const Wrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing[2]};
    width: 100%;
    height: 100%;
    padding: ${theme.spacing[5]} ${theme.spacing[4]};
  `
);

export const Header = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing[1]};
    width: 100%;
    min-width: 0;
  `
);

export const SupplierTitle = styled(Heading)(
  ({ theme }) => css`
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${textStyle(theme, 'headingSmall')};
  `
);

export const RatingLocationWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing[2]};
    width: 100%;
    min-width: 0;
  `
);

export const RatingBlock = styled.span(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    flex: 0 0 auto;
    gap: ${theme.spacing[2]};
  `
);

export const RatingsReviewsWrapper = styled.span`
  width: fit-content;
  display: inline-flex;
  align-items: center;
`;

export const RatingStar = styled.span(
  ({ theme }) => css`
    ${textStyle(theme, 'bodyMedium', 'mediumYellow')};
  `
);

export const LocationBlock = styled.span(
  ({ theme }) => css`
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${textStyle(theme, 'bodyMedium')};
  `
);

export const DescriptionWrapper = styled.div<{ isDark?: boolean }>(
  ({ theme, isDark }) => css`
    line-clamp: 4;
    -webkit-line-clamp: 4;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    ${textStyle(theme, 'bodyMedium', isDark ? 'lightGrey' : 'darkGrey')};
  `
);
